import {
  AfterViewInit,
  Component,
  ElementRef, EventEmitter, HostBinding,
  Input, OnChanges, OnDestroy,
  Output, SimpleChanges, TemplateRef,
  ViewChild
} from '@angular/core';
import {fromEvent} from "rxjs/internal/observable/fromEvent";
import {merge} from "rxjs/internal/observable/merge";
import {IDsTooltip} from "./ds-tooltip.model";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {CommonModule} from "@angular/common";
import {SafeHtmlPipe} from "../../pipes/safe-html";

type mouseEvents = 'mouseover' | 'mouseleave' | 'mouseenter';

@Component({
    templateUrl: './ds-tooltip.component.html',
    styleUrls: ['./ds-tooltip.component.scss'],
    imports: [CommonModule, SafeHtmlPipe],
})
export class DsTooltipComponent implements OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('tooltip') tooltipRef: ElementRef;

  @Input() host: HTMLElement;
  @Input() content?: string | number;
  @Input() template?: TemplateRef<any>;
  @Input() mode?: IDsTooltip['dsTooltipMode'];
  @Input() position: IDsTooltip['dsTooltipPosition'];
  @Input() align: IDsTooltip['dsTooltipAlign'];
  @Input() color: IDsTooltip['dsTooltipColor'];
  @Input() size: IDsTooltip['dsTooltipSize'];
  @Input() font: IDsTooltip['dsTooltipFont'];
  @Input() leftPxs: IDsTooltip['dsTooltipLeftPxs'] = 0; //left pixels
  @Input() topPxs: IDsTooltip['dsTooltipTopPxs'] = 0; //top pixels
  @Input() withShadow?: boolean;
  @Input() withBorder?: boolean;
  @Input() closeAnimation?: boolean;
  @Output() mouseEvent: EventEmitter<mouseEvents> = new EventEmitter<mouseEvents>();
  @Output() animationEnd: EventEmitter<void> = new EventEmitter<void>();
  @Input() hostClass: IDsTooltip['dsTooltipHostIndex'] = 'cover-all';
  @Input() zIndex: IDsTooltip['dsTooltipZIndex'];
  @Input() demoVideo?: boolean;
  @Input() openAnimation?: boolean;

  @HostBinding('style.z-index')
  get hostIndex() {
    return this.hostClass === 'cover-all' ? 99999999 : 999999;
  }

  @HostBinding('style.z-index')
  get hostZIndex() {
    return this.zIndex ?? this.hostClass === 'cover-all' ? 99999999 : 999999;
  }

  public tooltipStyle = {};
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _element: ElementRef,
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['content']) {
      this.content = changes['content'].currentValue;
    }
    if (changes && changes['template']) {
      this.content = changes['template'].currentValue;
    }
  }

  ngAfterViewInit() {
    this.mode === 'hoverStatic' && this._onMouseEvent();
    this._renderTooltip();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _onMouseEvent(): void {
    const eventTypes = ['mouseenter', 'mouseleave'];
    const events = eventTypes.map(e => fromEvent<Event>(this._element.nativeElement, e));
    const allEvents$ = merge(...events);
    allEvents$.pipe(
      takeUntil(this._destroyStream$)
    ).subscribe(e => this.mouseEvent.emit(<mouseEvents>e.type));
  }

  private _renderTooltip(): void {
    this.tooltipStyle = {};
    const hostBoundingBox: ClientRect = this.host.getBoundingClientRect();
    const tooltipBoundingBox: ClientRect = this.tooltipRef.nativeElement.getBoundingClientRect();
    const paddingSpace = 10;
    let top: number;
    let left: number;
    let padding: string;

    switch (this.position) {
      case 'top':
        left = hostBoundingBox.left - tooltipBoundingBox.width - this.leftPxs;
        if (hostBoundingBox.top - tooltipBoundingBox.height + paddingSpace > 0) {
          top = hostBoundingBox.top - tooltipBoundingBox.height - paddingSpace - this.topPxs;
          padding = `0 0 ${paddingSpace}px 0`;
        } else {
          top = hostBoundingBox.bottom - this.topPxs;
          padding = `${paddingSpace}px 0 0 0`;
        }
        break;

      case 'bottom':
        left = hostBoundingBox.left - tooltipBoundingBox.width - this.leftPxs;
        if (hostBoundingBox.bottom + tooltipBoundingBox.height + paddingSpace - this.topPxs <= window.innerHeight) {
          top = hostBoundingBox.bottom - this.topPxs;
          padding = `${paddingSpace}px 0 0 0`;
        } else {
          top = hostBoundingBox.top - tooltipBoundingBox.height - paddingSpace - this.topPxs;
          padding = `0 0 ${paddingSpace}px 0`;
        }
        break;

      case 'left':
        top = hostBoundingBox.top + hostBoundingBox.height / 2 - tooltipBoundingBox.height / 2;
        if (hostBoundingBox.left - tooltipBoundingBox.width - paddingSpace > 0) {
          left = hostBoundingBox.left - tooltipBoundingBox.width - paddingSpace - this.leftPxs;
          padding = `0 ${paddingSpace}px 0 0`;
        } else {
          left = hostBoundingBox.right - this.leftPxs;
          padding = `0 0 0 ${paddingSpace}px`;
        }
        break;

      case 'right':
        top = hostBoundingBox.top + hostBoundingBox.height / 2 - tooltipBoundingBox.height / 2;
        if (hostBoundingBox.right + tooltipBoundingBox.width + paddingSpace < window.innerWidth) {
          left = hostBoundingBox.right - this.leftPxs;
          padding = `0 0 0 ${paddingSpace}px`;
        } else {
          left = hostBoundingBox.left - tooltipBoundingBox.width - paddingSpace - this.leftPxs;
          padding = `0 ${paddingSpace}px 0 0`;
        }
        break;
    }

    if (this.position === 'top' || this.position === 'bottom') {
      switch (this.align) {
        case "center":
          left = hostBoundingBox.left + hostBoundingBox.width / 2 - tooltipBoundingBox.width / 2;
          break;
        case "left":
          if (hostBoundingBox.left + tooltipBoundingBox.width < window.innerWidth) {
            left = hostBoundingBox.left - this.leftPxs;
          } else {
            left = window.innerWidth - tooltipBoundingBox.width;
          }
          break;
        case "right":
          if (hostBoundingBox.right - tooltipBoundingBox.width > 0) {
            left = hostBoundingBox.right - this.leftPxs - tooltipBoundingBox.width;
          } else {
            left = hostBoundingBox.left - this.leftPxs;
          }
          break;
      }
    }

    if (this.position === 'left' || this.position === 'right') {
      switch (this.align) {
        case "top":
          if (hostBoundingBox.top + hostBoundingBox.height - tooltipBoundingBox.height > 0) {
            top = hostBoundingBox.top + hostBoundingBox.height - tooltipBoundingBox.height - this.topPxs;
          } else {
            top = hostBoundingBox.top - this.topPxs;
          }
          break;
        case "bottom":
          if (hostBoundingBox.top + tooltipBoundingBox.height < window.innerHeight) {
            top = hostBoundingBox.top - this.topPxs;
          } else {
            top = hostBoundingBox.top + hostBoundingBox.height - tooltipBoundingBox.height - this.topPxs;
          }
          break;
      }
    }

    this.tooltipStyle = {
      top: top + 'px',
      left: left + 'px',
      padding: padding
    };
  }
}
